import React, { useCallback, useMemo, useRef } from 'react';
import cn from 'clsx';
import {
  Button,
  SectionSubTitle,
  SectionTitle,
  SwiperWithNavigation,
  SwiperSlide,
  Typography,
  CategoryCardWithGAPromoClick,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey, enhanceImageUrl } from 'utils';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { Banner, useModelJsonSelector } from 'modules/model-json';
import {
  seedImage,
  seedInfo,
  seedItemTerm,
  seedItemValue,
  seedLink,
  seedSlide,
  seedsSection,
  seedsSectionDescription,
  seedsSectionTitle,
} from './seeds-section-styles';

/**
 * Вью секции с семенами
 */
export const SeedsSection = () => {
  const { seedsList } = useModelJsonSelector();

  const bannerRefs = useRef<HTMLAnchorElement[]>([]);
  const promotions = useMemo(
    () =>
      seedsList
        ? seedsList.map((seed: Banner, index: number) => ({
            name: 'sad_middle_семена',
            creative: `sad_${seed.title}`,
            position: (index + 1).toString(),
          }))
        : [],
    [seedsList]
  );

  usePromoViewEvent(bannerRefs, 'landingSad', promotions);

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = useCallback(() => {
    analyticsUtils.clickButtonEvent('landingSad', ClickButtonElementName.SwitchForward, 'sad_middle_семена');
  }, []);

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = useCallback(() => {
    analyticsUtils.clickButtonEvent(
      'landingSad',
      ClickButtonElementName.SwitchBackward,
      'sad_middle_семена'
    );
  }, []);

  if (!seedsList || seedsList.length === 0) {
    return null;
  }

  return (
    <section className={seedsSection}>
      <SectionTitle className={seedsSectionTitle}>Семена</SectionTitle>
      <Typography className={seedsSectionDescription} variant="l" size="body_short">
        Пора выращивать рассаду к&nbsp;новому дачному сезону
      </Typography>
      <SwiperWithNavigation
        onSlideNextTransitionEnd={handleNextSlideBtnClick}
        onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
        slidesPerView="auto">
        {seedsList.map(({ title, text, url, button, image, creative, name }: Banner, index: number) => (
          <SwiperSlide className={seedSlide} key={createElementKey('garden-seed', index)}>
            <CategoryCardWithGAPromoClick
              bgcolor={text}
              hoverbgcolor={text}
              bannerRefs={bannerRefs}
              className={cn(seedLink)}
              elementBlock="landingSad"
              href={url}
              position={index}
              promotion={promotions[index]}
              title={button}>
              <img
                className={seedImage}
                src={enhanceImageUrl(image)}
                alt={title}
                title={title}
                width="100"
                height="192"
                loading="lazy"
              />
              <div className={seedInfo}>
                <div>
                  <SectionSubTitle margin="m" variant="h3">
                    {title}
                  </SectionSubTitle>
                  <Typography className={seedItemTerm} variant="caption">
                    Всходы
                  </Typography>
                  <Typography className={seedItemValue} variant="s" size="body_accent">
                    {creative}
                  </Typography>
                  <Typography className={seedItemTerm} variant="caption">
                    Высадка в грунт
                  </Typography>
                  <Typography variant="s" size="body_accent">
                    {name}
                  </Typography>
                </div>
                <Button invert rounded wide>
                  Купить семена
                </Button>
              </div>
            </CategoryCardWithGAPromoClick>
          </SwiperSlide>
        ))}
      </SwiperWithNavigation>
    </section>
  );
};
